import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CurrencyInr, Eye, EyeSlash, EnvelopeSimple, ShieldCheck, LockSimple, TrendUp, Users } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import BrandLogo from "../components/BrandLogo";
import { useAuth, homeFor } from "../context/AuthContext";
import api, { apiError } from "../lib/api";

const BG = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg3NDE5MzMxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNote, setForgotNote] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter your login ID and password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(homeFor(user.role), { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { identifier: forgotEmail.trim(), note: forgotNote.trim() || null });
      toast.success(r.data?.message || "Reset request raised to KMC administration.");
      setForgotOpen(false);
      setForgotNote("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#030706] overflow-hidden">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-[#030706]/80" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 70% 20%, rgba(16,185,129,0.10) 0%, transparent 55%)" }} />

      <div className="relative z-10 min-h-screen flex items-center justify-center lg:justify-end px-5 sm:px-10 lg:px-24 xl:px-36 py-10">
        <div className="hidden lg:block absolute top-10 left-16 xl:left-24 z-10">
          <BrandLogo className="h-14 w-auto" testId="login-hero-logo" />
        </div>

        <div className="hidden lg:flex absolute left-16 xl:left-24 top-[35%] -translate-y-1/2 max-w-xl flex-col fade-up-slow">
          <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 font-semibold" data-testid="login-eyebrow">
            Wealth. Insights. Growth.
          </p>
          <h1 className="font-display text-4xl xl:text-5xl font-medium text-white leading-[1.12] tracking-tight mt-5">
            Your Goals.<br />Our Expertise.<br />
            <span className="text-emerald-400">Lasting Growth.</span>
          </h1>
          <div className="h-[3px] w-14 bg-emerald-500 rounded-full mt-6" />
          <p className="text-zinc-400 mt-6 text-sm leading-relaxed max-w-md">
            A private gateway to your investments — disciplined portfolio management,
            transparent performance and dedicated team, in one secure portal.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-4 max-w-xl" data-testid="login-trust-cards">
            {[
              ["₹250 Cr+", "Assets Under Management", "trust-aum", CurrencyInr],
              ["2,000+", "Trusted Clients", "trust-clients", Users],
              ["5+ Yrs", "Market Presence", "trust-years", TrendUp],
            ].map(([v, l, tid, Icon]) => (
              <div key={tid} data-testid={tid}
                className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-4 flex items-center gap-3 hover:border-emerald-500/25 transition-colors duration-300">
                <Icon size={24} weight="duotone" className="text-emerald-400 shrink-0" />
                <div>
                  <p className="tnum text-lg font-semibold leading-none tracking-tight text-emerald-300">{v}</p>
                  <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mt-0.5 leading-snug">{l}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-6">
            <LockSimple size={14} className="text-emerald-500/70" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
              Private &amp; Confidential- SEBI Compliant
            </p>
          </div>
        </div>

        <div className="w-full max-w-md fade-up" data-testid="login-panel">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl shadow-[0_24px_64px_rgba(0,0,0,0.6)] p-8 sm:p-10">
            <div className="flex justify-center">
              <BrandLogo className="h-16 w-auto" testId="login-logo" />
            </div>
            <div className="h-[2px] w-16 mx-auto bg-emerald-500/60 rounded-full mt-5" />

            <h2 className="font-display text-2xl text-white mt-7 text-center" data-testid="login-heading">Secure Investor Portal</h2>
            <p className="text-zinc-500 text-sm mt-1.5 mb-8 text-center">Sign in to access your private wealth dashboard.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-400/80">Email or Login ID</Label>
                <div className="relative">
                  <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input id="email" type="text" data-testid="login-email" placeholder="you@example.com or KMC-CL-001"
                    value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" className="glass-input h-11 pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-400/80">Password</Label>
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors duration-200">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <LockSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input id="password" type={showPw ? "text" : "password"} data-testid="login-password"
                    placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password" className="glass-input h-11 pl-10 pr-10" />
                  <button type="button" data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors duration-200"
                    onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeSlash size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              {error && (
                <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3.5 py-2.5">
                  {error}
                </div>
              )}
              <Button type="submit" data-testid="login-submit-button" disabled={busy}
                className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold tracking-wide emerald-glow transition-colors duration-200">
                {busy ? "Authenticating..." : "Sign In"}
              </Button>
            </form>

            <div className="flex items-center gap-2 mt-8 pt-6 border-t border-white/[0.06]">
              <ShieldCheck size={15} className="text-emerald-500/70" />
              <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-600 font-semibold" data-testid="secure-session-text">
                Secure Investor Portal · Private &amp; Confidential
              </p>
            </div>
          </div>
          <p className="text-center text-[11px] text-zinc-600 mt-6">
            Restricted access. Authorized clients and personnel only.
          </p>
          <div className="grid grid-cols-3 gap-2 mt-5 lg:hidden" data-testid="login-trust-cards-mobile">
            {[
              ["₹250 Cr+", "AUM", "trust-aum-m"],
              ["2,000+", "Clients", "trust-clients-m"],
              ["5+ Yrs", "Presence", "trust-years-m"],
            ].map(([v, l, tid]) => (
              <div key={tid} data-testid={tid}
                className="rounded-lg border border-white/10 bg-white/[0.03] backdrop-blur-xl px-3 py-3 text-center">
                <p className="tnum text-sm font-semibold text-emerald-300">{v}</p>
                <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Reset your password</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Enter your registered email or Login ID. A reset request will be raised to the KMC admin
              (info@kmcsolutions.co.in), who will verify your identity and share a new password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-4">
            <input type="text" required data-testid="forgot-email" placeholder="you@example.com or KMC-CL-001"
              value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="glass-input h-11" />
            <textarea rows={2} data-testid="forgot-note" placeholder="Note for admin (optional)"
              value={forgotNote} onChange={(e) => setForgotNote(e.target.value)} className="glass-input h-auto py-2.5 resize-none" />
            <Button type="submit" data-testid="forgot-submit" disabled={forgotBusy}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              {forgotBusy ? "Raising request..." : "Raise reset request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
